import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { format as formatDate } from "date-fns";
import { isEqual } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContractDurationType,
  type ContractDurationDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { ThemedDatePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { WholeNumberInput } from "../../../../../common/input/WholeNumberInput";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { useReadinessByField } from "../hooks/useReadinessByField";
import { FieldSaveError } from "./FieldSaveError";
import { useBlockStepLeave } from "./stepLeaveBlockers";

interface Props {
  eventId: string;
}

type EndType = "DATE" | "MONTHS";

// Denormalized intentionally: keeping inactive variant fields preserves user input
// across hasDefinedEnd / endType toggles. The single-source projection to a DTO
// happens in buildDuration().
interface ContractDurationDraft {
  startDate: string | null;
  endDate: string | null;
  periodMonths: number | null;
  hasDefinedEnd: boolean;
  endType: EndType;
}

const EMPTY_DRAFT: ContractDurationDraft = {
  startDate: null,
  endDate: null,
  periodMonths: null,
  hasDefinedEnd: false,
  endType: "DATE",
};

const buildDuration = (draft: ContractDurationDraft): ContractDurationDto | null => {
  if (!draft.hasDefinedEnd) {
    return {
      type: ContractDurationType.OpenEnded,
      startDate: draft.startDate,
      endDate: null,
      periodMonths: null,
    };
  }
  if (draft.endType === "DATE") {
    if (!draft.startDate || !draft.endDate) return null;
    return {
      type: ContractDurationType.FixedDates,
      startDate: draft.startDate,
      endDate: draft.endDate,
      periodMonths: null,
    };
  }
  if (draft.periodMonths == null || monthsRefusal(draft.periodMonths) !== null) return null;
  return {
    type: ContractDurationType.PeriodBased,
    startDate: draft.startDate,
    endDate: null,
    periodMonths: draft.periodMonths,
  };
};

const buildDraft = (duration?: ContractDurationDto | null): ContractDurationDraft => {
  if (!duration) return EMPTY_DRAFT;
  switch (duration.type) {
    case ContractDurationType.OpenEnded:
      return {
        startDate: duration.startDate ?? null,
        endDate: null,
        periodMonths: null,
        hasDefinedEnd: false,
        endType: "DATE",
      };
    case ContractDurationType.FixedDates:
      return {
        startDate: duration.startDate ?? null,
        endDate: duration.endDate ?? null,
        periodMonths: null,
        hasDefinedEnd: true,
        endType: "DATE",
      };
    case ContractDurationType.PeriodBased:
      return {
        startDate: duration.startDate ?? null,
        endDate: null,
        periodMonths: duration.periodMonths ?? null,
        hasDefinedEnd: true,
        endType: "MONTHS",
      };
  }
  return EMPTY_DRAFT;
};

type MonthsRefusal = "required" | "notWhole" | "tooSmall";

const monthsRefusal = (months: number | null): MonthsRefusal | null => {
  if (months == null) return "required";
  if (!Number.isInteger(months)) return "notWhole";
  if (months < 1) return "tooSmall";
  return null;
};

const draftShowsSaved = (draft: ContractDurationDraft, saved?: ContractDurationDto | null): boolean => {
  const shown = buildDuration(draft);
  if (saved == null) return shown === null || isEqual(shown, buildDuration(EMPTY_DRAFT));
  return isEqual(shown, buildDuration(buildDraft(saved)));
};

export const ContractDurationField = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const duration = state.eventData.contractDuration;
  const [draft, setDraft] = useState<ContractDurationDraft>(() => buildDraft(duration));
  const previousEventId = useRef(eventId);
  const previousDuration = useRef(duration);
  const { readinessFor } = useReadinessByField(eventId);
  const formatFieldError = useFieldErrorTranslation();

  useEffect(() => {
    const eventChanged = previousEventId.current !== eventId;
    const savedChanged = !isEqual(previousDuration.current, duration);
    previousEventId.current = eventId;
    previousDuration.current = duration;
    if (!eventChanged && !savedChanged) return;
    setDraft((current) =>
      eventChanged || !draftShowsSaved(current, duration)
        ? buildDraft(duration)
        : current,
    );
  }, [duration, eventId]);

  const saving = fieldStatus("eventData.contractDuration");
  const isSaving = saving === "saving" || saving === "pending";

  const save = (next: ContractDurationDraft) => {
    setDraft(next);
    const dto = buildDuration(next);
    if (dto !== null) updateField({ eventData: { contractDuration: dto } });
  };

  const showStartDateRequired = draft.hasDefinedEnd && draft.endType === "DATE" && !draft.startDate;
  const showEndDateRequired = draft.hasDefinedEnd && draft.endType === "DATE" && !draft.endDate;
  const monthsProblem =
    draft.hasDefinedEnd && draft.endType === "MONTHS" ? monthsRefusal(draft.periodMonths) : null;
  const monthsMessage: Record<MonthsRefusal, string> = {
    required: t("Number of months is required"),
    notWhole: t("Write a whole number of months"),
    tooSmall: t("Must be at least 1 month"),
  };

  const monthsBox = useRef<HTMLDivElement>(null);
  useBlockStepLeave("contract-duration-months", monthsProblem !== null && monthsProblem !== "required", () =>
    monthsBox.current?.querySelector<HTMLInputElement>("input")?.focus(),
  );

  const latestDraft = useRef({ draft, duration, save });
  latestDraft.current = { draft, duration, save };
  useEffect(() => {
    const saveTypedMonths = () => {
      const latest = latestDraft.current;
      if (!draftShowsSaved(latest.draft, latest.duration) && buildDuration(latest.draft) !== null) {
        latest.save(latest.draft);
      }
    };
    window.addEventListener("beforeunload", saveTypedMonths);
    return () => {
      window.removeEventListener("beforeunload", saveTypedMonths);
      saveTypedMonths();
    };
  }, []);

  const startDeadlineError = readinessFor("ContractStartsAfterBidSubmissionDeadline")?.error;
  const endDeadlineError = readinessFor("ContractEndsAfterBidSubmissionDeadline")?.error;
  const showStartInPast =
    startDeadlineError == null && draft.startDate != null && draft.startDate < formatDate(new Date(), "yyyy-MM-dd");

  return (
    <FormControl mb="8">
      <Stack spacing="5" align="stretch">
        <Box>
          <Flex align="center" gap="2" mb="2">
            <Text fontSize="sm" fontWeight="medium" color="smText">
              {t("Start date")}
            </Text>
            {isSaving && <Spinner size="xs" />}
          </Flex>
          <ThemedDatePicker
            date={draft.startDate}
            allowPastDates
            onChange={(date) => save({ ...draft, startDate: date ?? null })}
          />
          {showStartDateRequired && (
            <Text fontSize="xs" color="smError" mt="1">
              {t("Start date is required")}
            </Text>
          )}
          {showStartInPast && (
            <Text fontSize="xs" color="smStatusIssueText" mt="1" role="status">
              {t("The start date has passed. Check that it is the date you meant.")}
            </Text>
          )}
          {startDeadlineError && (
            <Text fontSize="xs" color="smError" mt="1">
              {formatFieldError(startDeadlineError)}
            </Text>
          )}
        </Box>

        <Flex align="center" justify="space-between" gap="4">
          <FormLabel mb="0">{t("Has the contract a defined end?")}</FormLabel>
          <ButtonGroup isAttached size="sm">
            <Button
              colorScheme={draft.hasDefinedEnd ? "teal" : "gray"}
              variant={draft.hasDefinedEnd ? "solid" : "outline"}
              onClick={() => save({ ...draft, hasDefinedEnd: true })}
            >
              {t("Yes")}
            </Button>
            <Button
              colorScheme={!draft.hasDefinedEnd ? "teal" : "gray"}
              variant={!draft.hasDefinedEnd ? "solid" : "outline"}
              onClick={() => save({ ...draft, hasDefinedEnd: false })}
            >
              {t("No")}
            </Button>
          </ButtonGroup>
        </Flex>

        {draft.hasDefinedEnd && (
          <Box>
            <RadioGroup value={draft.endType} onChange={(v) => save({ ...draft, endType: v as EndType })}>
              <Stack spacing="2">
                <SelectableCard as="label" isSelected={draft.endType === "DATE"} px="4" py="3" cursor="pointer">
                  <Flex align="center" gap="3">
                    <Radio value="DATE" colorScheme="teal" />
                    <Text fontWeight="medium" fontSize="sm">
                      {t("Specific end date")}
                    </Text>
                  </Flex>
                </SelectableCard>
                {draft.endType === "DATE" && (
                  <Box pl={{ base: "2", sm: "8" }} pb="1">
                    <ThemedDatePicker
                      date={draft.endDate}
                      allowPastDates
                      onChange={(date) => save({ ...draft, endDate: date ?? null })}
                    />
                    {showEndDateRequired && (
                      <Text fontSize="xs" color="smError" mt="1">
                        {t("End date is required")}
                      </Text>
                    )}
                  </Box>
                )}

                <SelectableCard as="label" isSelected={draft.endType === "MONTHS"} px="4" py="3" cursor="pointer">
                  <Flex align="center" gap="3">
                    <Radio value="MONTHS" colorScheme="teal" />
                    <Text fontWeight="medium" fontSize="sm">
                      {t("Number of months")}
                    </Text>
                  </Flex>
                </SelectableCard>
                {draft.endType === "MONTHS" && (
                  <Box ref={monthsBox} pl={{ base: "2", sm: "8" }} pb="1">
                    <WholeNumberInput
                      aria-label={String(t("Number of months"))}
                      aria-invalid={monthsProblem !== null && monthsProblem !== "required"}
                      numericValue={draft.periodMonths}
                      onNumericChange={(n) => setDraft({ ...draft, periodMonths: n })}
                      onBlur={() => !draftShowsSaved(draft, duration) && save(draft)}
                      maxW={{ base: "full", sm: "200px" }}
                    />
                    {monthsProblem && (
                      <Text fontSize="xs" color="smError" mt="1">
                        {monthsMessage[monthsProblem]}
                      </Text>
                    )}
                  </Box>
                )}
              </Stack>
            </RadioGroup>
            {endDeadlineError && (
              <Text fontSize="xs" color="smError" mt="1">
                {formatFieldError(endDeadlineError)}
              </Text>
            )}
          </Box>
        )}
      </Stack>
      <FieldSaveError eventId={eventId} path="eventData.contractDuration" />
    </FormControl>
  );
};
